import React, { useEffect, useState } from "react";
import { IconType } from "react-icons";
import {
  FaSpotify,
  FaApple,
  FaItunesNote,
  FaYoutube,
  FaSoundcloud,
  FaBandcamp,
  FaDeezer,
  FaAmazon,
  FaMusic,
} from "react-icons/fa6";
import { getPlaylists, Playlist } from "../services/playlistServices";

// Map a Strapi platform enum value to its brand icon.
const platformIcon = (platform: string): IconType => {
  const key = platform.toLowerCase();
  if (key.includes("spotify")) return FaSpotify;
  if (key.includes("apple")) return key.includes("itunes") ? FaItunesNote : FaApple;
  if (key.includes("youtube")) return FaYoutube;
  if (key.includes("soundcloud")) return FaSoundcloud;
  if (key.includes("bandcamp")) return FaBandcamp;
  if (key.includes("deezer")) return FaDeezer;
  if (key.includes("amazon")) return FaAmazon;
  return FaMusic; // Tidal / Other / anything unmapped
};

// Icon links per streaming platform.
function PlatformLinks({ links }: { links: Playlist["links"] }) {
  if (links.length === 0) return null;
  return (
    <div className="d-flex flex-wrap gap-3 mt-2">
      {links.map((link) => {
        const Icon = platformIcon(link.platform);
        return (
          <a
            key={`${link.platform}-${link.url}`}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            title={link.platform}
            aria-label={link.platform}
            className="text-primary fs-4"
          >
            <Icon />
          </a>
        );
      })}
    </div>
  );
}

function Playlists() {
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getPlaylists()
      .then((items) => {
        if (active) setPlaylists(items);
      })
      .catch(() => {
        if (active) setError("Couldn't load playlists right now. Please try again later.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <section className="py-5">
      <div className="container">
        <h2 className="fw-bold text-center mb-4">Playlists</h2>

        {loading && <p className="text-center text-muted">Loading playlists…</p>}
        {error && <p className="text-center text-danger">{error}</p>}
        {!loading && !error && playlists.length === 0 && (
          <p className="text-center text-muted">No playlists yet.</p>
        )}

        {/* Same grid + card markup as the Blog page for visual consistency. */}
        <div className="row">
          {playlists.map((playlist) => (
            <div className="col-md-6 col-lg-4 mb-4" key={playlist.id}>
              <div className="card shadow bnr-card h-100">
                {playlist.coverImageUrl && (
                  <img
                    src={playlist.coverImageUrl}
                    alt={playlist.title}
                    className="card-img-top"
                    style={{ height: "200px", objectFit: "cover" }}
                  />
                )}
                <div className="card-body">
                  <h5 className="card-title mt-2">{playlist.title}</h5>
                  {playlist.description && (
                    <p
                      className="text-muted"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {playlist.description}
                    </p>
                  )}
                  <PlatformLinks links={playlist.links} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Playlists;
